// src/components/auth/SignupSuccessView.tsx
import React from 'react';
import { CheckCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext.tsx';

interface SignupSuccessViewProps {
  signupEmail: string;
  loading: boolean;
  onResendEmail: () => void;
  onGoToSignIn: () => void;
}

const SignupSuccessView: React.FC<SignupSuccessViewProps> = ({
  signupEmail,
  loading,
  onResendEmail,
  onGoToSignIn,
}) => {
  const { darkMode } = useTheme();

  return (
    <div className="text-center py-4">
      <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
        <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
      </div>

      <h3 className={`font-display text-xl font-bold mb-2 ${darkMode ? 'text-orange-100' : 'text-slate-800'}`}>
        Check Your Email!
      </h3>

      <p className={`text-sm mb-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
        We sent a verification link to:
      </p>

      <div className={`mb-6 p-3 rounded-lg font-medium ${
        darkMode ? 'bg-slate-700 text-orange-300' : 'bg-orange-50 text-orange-700'
      }`}>
        {signupEmail}
      </div>

      <div className={`text-left space-y-2 mb-6 p-4 rounded-lg border ${
        darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'
      }`}>
        <p className={`font-semibold mb-2 ${darkMode ? 'text-orange-300' : 'text-slate-800'}`}>
          Next Steps:
        </p>
        <div className="space-y-2 text-sm">
          <div className="flex items-start space-x-2">
            <span className={darkMode ? 'text-orange-400' : 'text-orange-600'}>1.</span>
            <span className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
              Open your email inbox
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <span className={darkMode ? 'text-orange-400' : 'text-orange-600'}>2.</span>
            <span className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
              Click the "Verify Email" button
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <span className={darkMode ? 'text-orange-400' : 'text-orange-600'}>3.</span>
            <span className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
              Return here to sign in
            </span>
          </div>
        </div>
      </div>

      <div className={`text-xs space-y-2 mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        <p>No email? Check your spam folder</p>
        <p>Verification link expires in 24 hours</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onResendEmail}
          disabled={loading}
          className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
            darkMode
              ? 'border-orange-600 text-orange-300 hover:bg-orange-900/20'
              : 'border-orange-500 text-orange-600 hover:bg-orange-50'
          } disabled:opacity-50`}
        >
          {loading ? 'Sending...' : 'Resend Email'}
        </button>

        <button
          onClick={onGoToSignIn}
          className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
        >
          Go to Sign In
        </button>
      </div>
    </div>
  );
};

export default SignupSuccessView;
